// src/components/canine-teeth/ImageSelector.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import SubImageAnnotator from './SubImageAnnotator';
import CategorySelector from './CategorySelector';
import { FaDownload } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { saveSession } from '../../indexedDB';
import PropTypes from 'prop-types';
import * as UTIF from 'utif';



// All'inizio di ImageSelector.js, subito dopo gli import
const toothTypes = [
  "lower central incisor",
  "lower lateral incisor",
  "lower canine",
  "lower first premolar",
  "lower second premolar",
  "lower first molar",
  "lower second molar",
];

// Definizione dei titoli per ciascun punto
const pointTitles = [
  "Select lower central incisor tooth",
  "Select lower lateral incisor tooth",
  "Select lower canine tooth",
  "Select lower first premolar tooth",
  "Select lower second premolar tooth",
  "Select lower first molar tooth",
  "Select lower second molar tooth",
];

// Colori distintivi per ogni dente (7 colori diversi)
const toothColors = [
  '#FF6B6B', // Rosso per central incisor
  '#40d830ff', // Turchese per lateral incisor  
  '#2784f6ff', // Blu per canine
  '#d709fbff', // Verde chiaro per first premolar
  '#f9bf00ff', // Giallo per second premolar
  '#ffffffff', // Viola chiaro per first molar
  '#949494ff', // Arancione per second molar
];

function ImageSelector({
  imageFile,
  userEmail = '',
  tipo = '',
  posizione = '',
  sesso,
  realAge = '',
  selectionRequired = true,
  maxTotalPoints = 7,
  colorCategories = ['blue'],
  categoryColors = { blue: 'blue' },
  passCategoryToAnnotation = true,
  maxPointsPerCategory = {},
  useLeftRightInNames = false,
  categoriesToAnnotate = ['blue'],
  loadedSession = null,
  loadedSubImages = [],
  updateSessionsCount,
  userInfo, // Assicurati che userInfo sia passato correttamente
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [estAge, setEstAge] = useState(null);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [subImages, setSubImages] = useState([]);
  const [currentStep, setCurrentStep] = useState(
    selectionRequired ? 'selection' : 'annotation'
  );
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [loadedImage, setLoadedImage] = useState(null);
  const [category, setCategory] = useState(colorCategories[0]);
  const [mousePosition, setMousePosition] = useState(null);
  const [allAnnotations, setAllAnnotations] = useState({});
  const [annotatedImagesData, setAnnotatedImagesData] = useState({});
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const [draggingPointIndex, setDraggingPointIndex] = useState(null);
  const clickDisabled = useRef(false);
  // Add this state variable at the top of your component
  const [imageObjectUrl, setImageObjectUrl] = useState(null);

  // Stati per il ridimensionamento del rettangolo
  const [rectangleDimensions, setRectangleDimensions] = useState({});
  const [isDraggingCorner, setIsDraggingCorner] = useState(false);
  const [draggingCornerData, setDraggingCornerData] = useState(null);
  const [hoveredCorner, setHoveredCorner] = useState(null);

  

  useEffect(() => {
    if (loadedSession) {
      if (loadedSubImages && loadedSubImages.length > 0) {
        setSubImages(loadedSubImages);

        // Inizializza tutte le annotazioni
        const annotations = {};
        loadedSubImages.forEach((subImg) => {
          annotations[subImg.id] = subImg.annotations || {}; // Assicurati che esistano le annotazioni
        });
        setAllAnnotations(annotations);
        setCurrentStep('annotation');

        // Imposta le immagini escluse
        const excluded = loadedSubImages.filter(subImg => subImg.isExcluded).map(subImg => subImg.id);
        setExcludedSubImages(excluded);
        console.log('Excluded SubImages:', excluded);

        // Inizializza lineLengths dai ratios delle annotazioni
        const initialLineLengths = {};
        loadedSubImages.forEach((subImg) => {
          if (subImg.annotations && subImg.annotations.ratios) {
            // Verifica se ratios è un numero, altrimenti prova a convertirlo
            const ratio = typeof subImg.annotations.ratios === 'number'
              ? subImg.annotations.ratios
              : subImg.annotations.ratios?.overallRatio
                ? parseFloat(subImg.annotations.ratios.overallRatio)
                : parseFloat(subImg.annotations.ratios);
            if (!isNaN(ratio)) {
              initialLineLengths[subImg.id] = ratio;
              console.log(`Loaded ratio for subImageId ${subImg.id}:`, ratio);
            } else {
              console.error(`Invalid ratio for subImageId ${subImg.id}:`, subImg.annotations.ratios);
            }
          }
        });
        setLineLengths(initialLineLengths);
        console.log('Initial lineLengths after recovery:', initialLineLengths);
      } else {
        // Gestione per una singola sessione caricata
        const newSubImages = [{ subImage: loadedSession }];
        setSubImages(newSubImages); // Passa l'intero oggetto sessione all'interno di subImage
        setCurrentStep('annotation');
        setAllAnnotations({ [loadedSession.id]: loadedSession.annotations || {} }); // Usa l'ID corretto

        // Imposta lo stato di esclusione se presente
        if (loadedSession.isExcluded) {
          setExcludedSubImages([loadedSession.id]);
          console.log('Single session excluded:', loadedSession.id);
        }

        // Inizializza lineLengths se presenti
        if (loadedSession.annotations && loadedSession.annotations.ratios) {
          const ratio = typeof loadedSession.annotations.ratios === 'number'
            ? loadedSession.annotations.ratios
            : parseFloat(loadedSession.annotations.ratios.overallRatio || loadedSession.annotations.ratios);
          if (!isNaN(ratio)) {
            setLineLengths({ [loadedSession.id]: ratio });
            console.log(`Loaded ratio for loadedSession.id ${loadedSession.id}:`, ratio);
          } else {
            console.error(`Invalid ratio for loadedSession.id ${loadedSession.id}:`, loadedSession.annotations.ratios);
          }
        }

        // Debug ratios
        console.log(`Single SubImage ID: ${loadedSession.id}, Ratios:`, loadedSession.annotations.ratios);
      }
    }
  }, [loadedSession, loadedSubImages]);




  useEffect(() => {
    if (!imageFile) return;

    const fileType = imageFile.type;

    if (fileType === 'image/tiff' || fileType === 'image/x-tiff') {
      // Handle TIFF images using UTIF.js
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const ifds = UTIF.decode(arrayBuffer);

        // Use UTIF.decodeImage instead of UTIF.decodeImages
        UTIF.decodeImage(arrayBuffer, ifds[0]); // Decode the first image/frame

        const firstPage = ifds[0];
        const rgba = UTIF.toRGBA8(firstPage);

        const canvas = document.createElement('canvas');
        canvas.width = firstPage.width;
        canvas.height = firstPage.height;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.createImageData(canvas.width, canvas.height);
        imageData.data.set(rgba);
        ctx.putImageData(imageData, 0, 0);

        const dataURL = canvas.toDataURL();

        const img = new Image();
        img.onload = () => {
          setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
          setLoadedImage(img);

          if (!selectionRequired && !loadedSession) {
            const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

            const subImg = {
              subImage: {
                id: 1,
                name: `${nameBase}.jpg`,
                dataURL: dataURL,
                center: { x: img.width / 2, y: img.height / 2 },
                category: passCategoryToAnnotation ? null : undefined,
                originalPosition: { x: 0, y: 0 },
                originalImageName: imageFile.name,
                tool: 'original-image',
                email: userEmail,
                tipo,
                posizione,
                sesso,
                timestamp: new Date().toISOString(),
                isExcluded: false,
              },
            };

            setSubImages([subImg]);
            setCurrentStep('annotation');
          }
        };
        img.src = dataURL;
      };
      reader.readAsArrayBuffer(imageFile);
    } else {
      // Handle other image types (JPEG, PNG, etc.)
      const img = new Image();

      img.onload = () => {
        setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
        setLoadedImage(img);

        if (!selectionRequired && !loadedSession) {
          const dataURL = img.src;
          const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

          const subImg = {
            subImage: {
              id: 1,
              name: `${nameBase}.jpg`,
              dataURL: dataURL,
              center: { x: img.width / 2, y: img.height / 2 },
              category: passCategoryToAnnotation ? null : undefined,
              originalPosition: { x: 0, y: 0 },
              originalImageName: imageFile.name,
              tool: 'original-image',
              email: userEmail,
              tipo,
              posizione,
              sesso,
              timestamp: new Date().toISOString(),
              isExcluded: false,
            },
          };

          setSubImages([subImg]);
          setCurrentStep('annotation');
        }
      };

      const objectUrl = URL.createObjectURL(imageFile);
      img.src = objectUrl;

      // Store the object URL in state
      setImageObjectUrl(objectUrl);
    }
  }, [
    imageFile,
    selectionRequired,
    passCategoryToAnnotation,
    loadedSession,
    userEmail,
    tipo,
    posizione,
    sesso,
  ]);


  // Add a cleanup useEffect to revoke the object URL when the component unmounts
  useEffect(() => {
    return () => {
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    };
  }, [imageObjectUrl]);


  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
  };

  // Funzione per ottenere le dimensioni del rettangolo per un punto specifico
  const getRectDimensions = useCallback((pointIndex) => {
    return rectangleDimensions[pointIndex] || { width: 250, height: 350 };
  }, [rectangleDimensions]);

  // Funzione per aggiornare le dimensioni del rettangolo
  const updateRectDimensions = useCallback((pointIndex, newDimensions) => {
    setRectangleDimensions(prev => ({
      ...prev,
      [pointIndex]: newDimensions
    }));
  }, []);

  // Funzione per calcolare le coordinate degli angoli del rettangolo
  const getRectCorners = useCallback((point, pointIndex, scaleX, scaleY) => {
    const rect = getRectDimensions(pointIndex);
    const centerX = point.x * scaleX;
    const centerY = point.y * scaleY;
    const rectWidth = rect.width * scaleX;
    const rectHeight = rect.height * scaleY;
    
    const left = centerX - rectWidth / 2;
    const top = centerY - rectHeight / 2;
    const right = left + rectWidth;
    const bottom = top + rectHeight;

    return {
      topLeft: { x: left, y: top },
      topRight: { x: right, y: top },
      bottomLeft: { x: left, y: bottom },
      bottomRight: { x: right, y: bottom },
      center: { x: centerX, y: centerY },
      width: rectWidth,
      height: rectHeight
    };
  }, [getRectDimensions]);

  // Funzione per verificare se il mouse è sopra un angolo
  const getHoveredCorner = useCallback((mouseX, mouseY, corners, pointIndex) => {
    const threshold = 10; // Pixel di tolleranza per il hover
    
    const cornerPositions = [
      { name: 'topLeft', pos: corners.topLeft },
      { name: 'topRight', pos: corners.topRight },
      { name: 'bottomLeft', pos: corners.bottomLeft },
      { name: 'bottomRight', pos: corners.bottomRight }
    ];

    for (let corner of cornerPositions) {
      const dx = mouseX - corner.pos.x;
      const dy = mouseY - corner.pos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance <= threshold) {
        return { pointIndex, corner: corner.name, pos: corner.pos };
      }
    }
    
    return null;
  }, []);

  const handleImageClick = (e) => {
    if (isDraggingPoint) return;
    if (clickDisabled.current) {
      clickDisabled.current = false;
      return;
    }

    const totalPoints = selectedPoints.length;
    if (totalPoints >= maxTotalPoints) {
      alert(`Hai selezionato il numero massimo di ${maxTotalPoints} punti.`);
      return;
    }

    const pointsInCategory = selectedPoints.filter((p) => p.category === category)
      .length;
    const categoryMax = maxPointsPerCategory[category] || maxTotalPoints;

    if (pointsInCategory >= categoryMax) {
      alert(`Hai raggiunto il numero massimo di punti per la categoria ${category}.`);
      return;
    }


  const canvas = canvasRef.current;
  const img = imageRef.current;
  if (!canvas || !img) return;

  // Use image bounding rect so coordinates match the visible image (account for centering)
  const imgRect = img.getBoundingClientRect();
  const x = (e.clientX - imgRect.left);
  const y = (e.clientY - imgRect.top);

  // Convert coordinates to original image pixel space
  const proportionX = imageSize.width / img.clientWidth;
  const proportionY = imageSize.height / img.clientHeight;

  const finalX = x * proportionX;
  const finalY = y * proportionY;

    setSelectedPoints((prevPoints) => [...prevPoints, { x: finalX, y: finalY, category }]);
  };

  const handleMouseDown = (e) => {
  const canvas = canvasRef.current;
  const img = imageRef.current;
  if (!canvas || !img) return;

  const imgRect = img.getBoundingClientRect();
  const x = (e.clientX - imgRect.left);
  const y = (e.clientY - imgRect.top);
  const proportionX = imageSize.width / img.clientWidth;
  const proportionY = imageSize.height / img.clientHeight;
  const finalX = x * proportionX;
  const finalY = y * proportionY;

    // Verifica se stiamo cliccando su un angolo del rettangolo
    const scaleX = canvas.clientWidth / imageSize.width;
    const scaleY = canvas.clientHeight / imageSize.height;
    
    for (let i = 0; i < selectedPoints.length; i++) {
      const point = selectedPoints[i];
      if (categoriesToAnnotate.includes(point.category)) {
        const corners = getRectCorners(point, i, scaleX, scaleY);
        const hoveredCorner = getHoveredCorner(x, y, corners, i);
        
        if (hoveredCorner) {
          setIsDraggingCorner(true);
          setDraggingCornerData(hoveredCorner);
          clickDisabled.current = true;
          return;
        }
      }
    }

    // Verifica se stiamo cliccando su un punto esistente per trascinarlo
    selectedPoints.forEach((point, index) => {
      const dx = finalX - point.x;
      const dy = finalY - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= 10) {
        setIsDraggingPoint(true);
        setDraggingPointIndex(index);
        setMousePosition(null);
        clickDisabled.current = false;
      }
    });
  };

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;

  const imgRect = img.getBoundingClientRect();
  const x = (e.clientX - imgRect.left);
  const y = (e.clientY - imgRect.top);
  const proportionX = imageSize.width / img.clientWidth;
  const proportionY = imageSize.height / img.clientHeight;
  const finalX = x * proportionX;
  const finalY = y * proportionY;

    // Se stiamo trascinando un angolo
    if (isDraggingCorner && draggingCornerData) {
      const { pointIndex, corner } = draggingCornerData;
      const point = selectedPoints[pointIndex];
      const currentRect = getRectDimensions(pointIndex);
      
      // Calcola le nuove dimensioni basate sulla posizione del mouse
      const centerX = point.x;
      const centerY = point.y;
      
      let newWidth = currentRect.width;
      let newHeight = currentRect.height;
      
      switch (corner) {
        case 'topLeft':
          newWidth = Math.abs((centerX - finalX) * 2);
          newHeight = Math.abs((centerY - finalY) * 2);
          break;
        case 'topRight':
          newWidth = Math.abs((finalX - centerX) * 2);
          newHeight = Math.abs((centerY - finalY) * 2);
          break;
        case 'bottomLeft':
          newWidth = Math.abs((centerX - finalX) * 2);
          newHeight = Math.abs((finalY - centerY) * 2);
          break;
        case 'bottomRight':
          newWidth = Math.abs((finalX - centerX) * 2);
          newHeight = Math.abs((finalY - centerY) * 2);
          break;
        default:
          // Mantieni le dimensioni attuali se il corner non è riconosciuto
          break;
      }
      
      // Limita le dimensioni minime e massime
      newWidth = Math.max(50, Math.min(newWidth, imageSize.width));
      newHeight = Math.max(50, Math.min(newHeight, imageSize.height));
      
      updateRectDimensions(pointIndex, { width: newWidth, height: newHeight });
      clickDisabled.current = true;
      return;
    }

    // Se stiamo trascinando un punto
    if (isDraggingPoint && draggingPointIndex !== null) {
      setSelectedPoints((prevPoints) => {
        const newPoints = [...prevPoints];
        newPoints[draggingPointIndex] = { ...newPoints[draggingPointIndex], x: finalX, y: finalY };
        return newPoints;
      });
      clickDisabled.current = true;
      return;
    }

    // Gestione del hover sugli angoli e sui punti
    let isOverPoint = false;
    let isOverCorner = false;
    let cursorStyle = 'crosshair';

    // Verifica hover sui punti
    selectedPoints.forEach((point) => {
      const dx = finalX - point.x;
      const dy = finalY - point.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance <= 10) {
        isOverPoint = true;
        cursorStyle = 'move';
      }
    });

    // Verifica hover sugli angoli dei rettangoli
    if (!isOverPoint) {
      const scaleX = canvas.clientWidth / imageSize.width;
      const scaleY = canvas.clientHeight / imageSize.height;
      
      for (let i = 0; i < selectedPoints.length; i++) {
        const point = selectedPoints[i];
        if (categoriesToAnnotate.includes(point.category)) {
          const corners = getRectCorners(point, i, scaleX, scaleY);
          const hoveredCorner = getHoveredCorner(x, y, corners, i);
          
          if (hoveredCorner) {
            isOverCorner = true;
            cursorStyle = 'nw-resize';
            setHoveredCorner(hoveredCorner);
            break;
          }
        }
      }
      
      if (!isOverCorner) {
        setHoveredCorner(null);
      }
    }

    canvas.style.cursor = cursorStyle;

    if (!isDraggingPoint && !isDraggingCorner) {
      setMousePosition({ x: finalX, y: finalY });
    }
  };

  const handleMouseUp = () => {
    setIsDraggingPoint(false);
    setDraggingPointIndex(null);
    setIsDraggingCorner(false);
    setDraggingCornerData(null);
  };

  const handleMouseLeave = () => {
    setMousePosition(null);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }
  };

  const handleUndoLastPoint = () => {
    if (selectedPoints.length === 0) {
      alert('Nessun punto da annullare.');
      return;
    }
    const pointIndex = selectedPoints.length - 1;
    
    // Rimuovi le dimensioni personalizzate per questo punto
    setRectangleDimensions(prev => {
      const updated = { ...prev };
      delete updated[pointIndex];
      return updated;
    });
    
    setSelectedPoints((prevPoints) => prevPoints.slice(0, -1));
  };

  const handleResetPoints = () => {
    setSelectedPoints([]);
    setRectangleDimensions({});
  };

  const handleVerification = () => {
    if (selectedPoints.length === 7) {
      createSubImages(); // Crea le sotto-immagini direttamente
      setCurrentStep('annotation'); // Passa subito all'annotazione
    } else {
      alert(`Devi selezionare esattamente ${maxTotalPoints} punti.`);
    }
  };

  const createSubImages = async () => {
    setIsLoading(true);
    try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
  
    // Itera sui selectedPoints usando il loro indice originale così
    // le dimensioni personalizzate e il tipo di dente corrispondono all'indice reale
    const pointsToAnnotate = selectedPoints;

    const newSubImages = [];
    for (let i = 0; i < pointsToAnnotate.length; i++) {
      const point = pointsToAnnotate[i];
      if (!categoriesToAnnotate.includes(point.category)) continue;

      // Usa le dimensioni personalizzate del rettangolo per questo punto (indice originale i)
      const rectDimensions = getRectDimensions(i);
      const rectWidth = rectDimensions.width;
      const rectHeight = rectDimensions.height;

      let sx = Math.round(point.x - rectWidth / 2);
      let sy = Math.round(point.y - rectHeight / 2);
      const sWidth = rectWidth;
      const sHeight = rectHeight;

      if (sx < 0) sx = 0;
      if (sy < 0) sy = 0;
      if (sx + sWidth > imageSize.width) sx = imageSize.width - sWidth;
      if (sy + sHeight > imageSize.height) sy = imageSize.height - sHeight;

      canvas.width = rectWidth;
      canvas.height = rectHeight;

      ctx.drawImage(
        loadedImage,
        sx,
        sy,
        sWidth,
        sHeight,
        0,
        0,
        rectWidth,
        rectHeight
      );

      const dataURL = canvas.toDataURL('image/jpeg', 1);
      const nameBase = imageFile.name.replace(/\.[^/.]+$/, '');

      // Ottieni il tipo di dente basato sull'indice originale
      const toothType = toothTypes[i] || toothTypes[i % toothTypes.length];

      // Formatta il tipo di dente per il nome del file (rimuovi spazi)
      const formattedToothType = toothType.replace(/ /g, '_');

      // Genera il nuovo nome della sotto-immagine
      const subImageName = `${nameBase}_${formattedToothType}_${newSubImages.length + 1}.jpg`;

      newSubImages.push({
        id: newSubImages.length + 1,
        subImage: {
          name: subImageName,
          dataURL,
          center: { x: point.x, y: point.y },
          category: passCategoryToAnnotation ? point.category : undefined,
          originalPosition: { x: sx, y: sy },
          originalImageName: imageFile.name,
          tool: 'european-formula',
          email: userEmail,
          tipo,
          posizione,
          sesso,
          realAge,
          timestamp: new Date().toISOString(),
          isExcluded: false,
          toothType: toothType,
          customDimensions: { width: rectWidth, height: rectHeight },
        },
      });
    }
  
    // Save each sub-image session
    await Promise.all(
      newSubImages.map(async (subImageData) => {
        const sessionData = {
          subImage: subImageData.subImage,
          tool: subImageData.subImage.tool,
          email: subImageData.subImage.email,
          tipo: subImageData.subImage.tipo,
          posizione: subImageData.subImage.posizione,
          sesso: subImageData.subImage.sesso,
          realAge: subImageData.subImage.realAge,
          isExcluded: subImageData.subImage.isExcluded,
          timestamp: subImageData.subImage.timestamp,
        };
        await saveSession(sessionData);
      })
    );
  
    // Save the original image session
    const originalCanvas = document.createElement('canvas');
    const originalCtx = originalCanvas.getContext('2d');
  
    originalCanvas.width = imageSize.width;
    originalCanvas.height = imageSize.height;
  
    originalCtx.drawImage(loadedImage, 0, 0);
  
    const originalDataURL = originalCanvas.toDataURL('image/jpeg', 1);
  
    const originalSessionData = {
      subImage: {
        name: imageFile.name,
        dataURL: originalDataURL,
        center: { x: 0, y: 0 },
        category: null,
        originalPosition: { x: 0, y: 0 },
        originalImageName: imageFile.name,
      },
      tool: 'original-image',
      email: userEmail,
      tipo,
      posizione,
      sesso,
      realAge,
      timestamp: new Date().toISOString(),
    };
  
    await saveSession(originalSessionData);
  
    setSubImages(newSubImages);
    setCurrentStep('annotation');
  } catch (error) {
    console.error('Errore durante il salvataggio delle sessioni:', error);
    alert('Si è verificato un errore durante il salvataggio. Per favore riprova.');
  } finally {
    setIsLoading(false);
  }
  };

  const collectAnnotations = useCallback((subImageId, annotations, annotatedImageDataURL) => {
    setAllAnnotations((prevAnnotations) => ({
      ...prevAnnotations,
      [subImageId]: annotations,
    }));

    if (annotatedImageDataURL) {
      setAnnotatedImagesData((prevData) => ({
        ...prevData,
        [subImageId]: annotatedImageDataURL,
      }));
    }
  }, []);

  const handleDownloadAnnotationsJSON = () => {
    const secondPremolar = subImages.find(subImg =>
      subImg.subImage.name.includes("second_premolar_5")
    );
    const F4_F5 = secondPremolar && typeof lineLengths[secondPremolar.id] === 'number'
      ? lineLengths[secondPremolar.id].toFixed(2)
      : 'N/A';

    const annotationsArray = subImages.map((subImage) => {
      const subImageAnnotations = allAnnotations[subImage.id] || {};
      return {
        imageName: subImage.subImage.name || '',
        annotations: subImageAnnotations.coordinates || {},
        ratios: subImageAnnotations.ratios || {},
        areas: subImageAnnotations.areas || {},
        estimatedAge: subImageAnnotations.estimatedAge || null,
        email: userEmail || '',
        tipo: tipo || '',
        posizione: posizione || '',
        sesso: sesso || '',
        realAge: realAge || '',
        centerPoint: subImage.subImage.center || {},
        timestamp: subImageAnnotations.timestamp || '',
        subImageDimensions: { width: 250, height: 350 },
        isExcluded: excludedSubImages.includes(subImage.id),
        // Aggiungi F4_F5 se questa è la sotto-immagine desiderata
        est_age: subImage.subImage.name.includes("second_premolar_5") ? F4_F5 : undefined,
        tool: 'european-formula',
      };
    });

    const data = {
      originalImageName: imageFile
        ? imageFile.name
        : loadedSession.subImage.originalImageName || '',
      annotations: annotationsArray,
      est_age: estAge !== null && estAge !== 'N/A' ? parseFloat(estAge) : null,
      realAge: realAge || '',
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    saveAs(blob, 'annotations.json');
  };

  // Aggiungi queste costanti per mappare i colori delle linee
  const lineKeyToColor = {
    line1: 'blue',
    line2: 'red',
    line3: 'green',
  };

  const getLineColor = (lineKey) => {
    return lineKeyToColor[lineKey] || '#f0f0f0';
  };

  const lineThickness = 1;
  const pointRadius = 2;

  const handleDownloadAnnotatedImages = () => {
    if (subImages.length === 0) {
      alert('Non ci sono immagini annotate da scaricare.');
      return;
    }

    const zip = new JSZip();
    const imgFolder = zip.folder('annotated_images');

    const promises = subImages.map(async (subImage) => {
      let annotatedDataURL = annotatedImagesData[subImage.id];
      if (!annotatedDataURL) {
        // Genera l'immagine annotata
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Dimensioni della sotto-immagine
        canvas.width = 250;
        canvas.height = 350;

        // Carica l'immagine della sotto-immagine
        const img = new Image();
        img.src = subImage.subImage.dataURL;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Disegna l'immagine sul canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Ottieni le annotazioni
        const annotations = allAnnotations[subImage.id];

        // Disegna le annotazioni sul canvas
        if (annotations && annotations.coordinates) {
          const coordinates = annotations.coordinates;
          Object.entries(coordinates).forEach(([lineKey, points]) => {
            // Ottieni il colore basato su lineKey
            const color = getLineColor(lineKey);
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = lineThickness;

            if (points.length > 0) {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i].x, points[i].y);
              }
              ctx.stroke();

              points.forEach((point) => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, pointRadius, 0, 2 * Math.PI);
                ctx.fill();
              });
            }
          });
        }

        // Ottieni il data URL dell'immagine annotata
        annotatedDataURL = canvas.toDataURL('image/jpeg', 1);
        // Salva l'immagine annotata nei dati
        setAnnotatedImagesData((prevData) => ({
          ...prevData,
          [subImage.id]: annotatedDataURL,
        }));
      }

      // Aggiungi l'immagine annotata allo ZIP
      return fetch(annotatedDataURL)
        .then((res) => res.blob())
        .then((blob) => {
          imgFolder.file(subImage.subImage.name, blob, { binary: true });
        });
    });

    
    // Includi l'immagine originale nello ZIP
    if (imageFile) {
      zip.file(imageFile.name, imageFile);
    } else if (loadedSession && loadedSession.subImage && loadedSession.subImage.dataURL) {
      // Se non hai accesso a imageFile, usa l'immagine dal loadedSession
      const imgDataURL = loadedSession.subImage.dataURL;
      const imgName = loadedSession.subImage.name || 'original_image.jpg';
      const imgBlob = dataURLtoBlob(imgDataURL);
      zip.file(imgName, imgBlob, { binary: true });
    }

    Promise.all(promises)
      .then(() => {
        zip.generateAsync({ type: 'blob' }).then((content) => {
          saveAs(content, 'annotated_images.zip');
        });
      })
      .catch((err) => {
        console.error('Errore nel creare lo ZIP:', err);
        alert("C'è stato un errore nel creare il file ZIP.");
      });
  };

  // Funzione per convertire dataURL in Blob
  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;

    if (canvas && img) {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const imgWidth = img.clientWidth;
      const imgHeight = img.clientHeight;

  // Set canvas internal pixel buffer to match displayed image size
  canvas.width = imgWidth;
  canvas.height = imgHeight;

  // Ensure the canvas element is positioned and sized exactly over the image
  // so drawing and mouse events align with the visible image.
  canvas.style.position = 'absolute';
  canvas.style.left = `${img.offsetLeft}px`;
  canvas.style.top = `${img.offsetTop}px`;
  canvas.style.width = `${imgWidth}px`;
  canvas.style.height = `${imgHeight}px`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Non ridisegnamo l'immagine, manteniamo il canvas trasparente come overlay

      const scaleX = imgWidth / imageSize.width;
      const scaleY = imgHeight / imageSize.height;

      selectedPoints.forEach((point, pointIndex) => {
        // Usa il colore specifico per ogni dente basato sull'indice
        const toothColor = toothColors[pointIndex] || '#000000';
        
        // Disegna il punto centrale
        ctx.fillStyle = toothColor;
        ctx.beginPath();
        ctx.arc(point.x * scaleX, point.y * scaleY, 5, 0, 2 * Math.PI);
        ctx.fill();

        if (categoriesToAnnotate.includes(point.category)) {
          const rectDimensions = getRectDimensions(pointIndex);
          const rectWidth = rectDimensions.width * scaleX;
          const rectHeight = rectDimensions.height * scaleY;
          
          // Disegna il rettangolo di selezione con il colore specifico del dente
          ctx.strokeStyle = toothColor;
          ctx.lineWidth = 2;
          ctx.setLineDash([10, 5]);
          ctx.strokeRect(
            (point.x - rectDimensions.width / 2) * scaleX,
            (point.y - rectDimensions.height / 2) * scaleY,
            rectWidth,
            rectHeight
          );
          ctx.setLineDash([]);

          // Disegna i punti di controllo degli angoli
          const corners = getRectCorners(point, pointIndex, scaleX, scaleY);
          const cornerSize = 8;
          
          ctx.fillStyle = 'white';
          ctx.strokeStyle = toothColor;
          ctx.lineWidth = 2;

          // Disegna i quadratini negli angoli
          [corners.topLeft, corners.topRight, corners.bottomLeft, corners.bottomRight].forEach((corner, cornerIndex) => {
            ctx.fillRect(corner.x - cornerSize/2, corner.y - cornerSize/2, cornerSize, cornerSize);
            ctx.strokeRect(corner.x - cornerSize/2, corner.y - cornerSize/2, cornerSize, cornerSize);
          });

          // Evidenzia l'angolo su cui si sta facendo hover
          if (hoveredCorner && hoveredCorner.pointIndex === pointIndex) {
            ctx.fillStyle = toothColor;
            const hoveredPos = corners[hoveredCorner.corner];
            ctx.fillRect(hoveredPos.x - cornerSize/2, hoveredPos.y - cornerSize/2, cornerSize, cornerSize);
          }
        }
      });

      // Mostra il rettangolo di anteprima quando si muove il mouse
      if (
        !isDraggingPoint &&
        !isDraggingCorner &&
        mousePosition &&
        selectedPoints.length < maxTotalPoints
      ) {
        const { x, y } = mousePosition;
        // Usa il colore per il prossimo punto che verrà selezionato
        const nextPointCategory = colorCategories[selectedPoints.length] || colorCategories[0];
        ctx.strokeStyle = categoryColors[nextPointCategory] || 'black';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);
        const rectWidth = 250 * scaleX;
        const rectHeight = 350 * scaleY;
        ctx.strokeRect(
          (x - 125) * scaleX,
          (y - 175) * scaleY,
          rectWidth,
          rectHeight
        );
        ctx.setLineDash([]);
      }
    }
  }, [
    selectedPoints,
    mousePosition,
    category,
    categoryColors,
    categoriesToAnnotate,
    imageSize,
    isDraggingPoint,
    isDraggingCorner,
    rectangleDimensions,
    hoveredCorner,
    getRectDimensions,
    getRectCorners,
    colorCategories,
    maxTotalPoints,
  ]);

  // Stati per la gestione delle esclusioni e delle lunghezze delle linee
  const [excludedSubImages, setExcludedSubImages] = useState([]);
  const [lineLengths, setLineLengths] = useState({});

  // Memorizza handleExcludeChange con useCallback
  const handleExcludeChange = useCallback((subImageId, isExcluded) => {
    setExcludedSubImages((prev) => {
      if (isExcluded) {
        // Aggiungi solo se l'ID non è già presente
        if (!prev.includes(subImageId)) {
          return [...prev, subImageId];
        }
        return prev;
      } else {
        // Rimuovi l'ID se presente
        return prev.filter((id) => id !== subImageId);
      }
    });
  }, []);

  // Memorizza handleRatiosChange con useCallback
  const handleRatiosChange = useCallback((subImageId, ratio) => {
    console.log(`handleRatiosChange ricevuto per subImageId ${subImageId}:`, ratio);
    
    let numeric = ratio;
    if (typeof ratio === 'object' && ratio !== null) {
      // es. prendi il rapporto complessivo calcolato in SubImageAnnotator
      const line1 = ratio.line1 || 0;
      const line2 = ratio.line2 || 0;
      const line3 = ratio.line3 || 0;
      
      if (ratio.overallRatio !== undefined) {
        numeric = ratio.overallRatio;
      } else if (line1 > 0) {
        numeric = (line2 + line3) / line1;
      } else {
        numeric = 0;
      }
    }
    
    console.log(`handleRatiosChange: valore numerico calcolato:`, numeric);
    
    // Verifica che il valore sia un numero valido
    if (typeof numeric === 'number' && !isNaN(numeric) && isFinite(numeric)) {
      console.log(`handleRatiosChange: salvando valore valido per subImageId ${subImageId}:`, numeric);
      setLineLengths(prev => {
        const updated = { ...prev, [subImageId]: numeric };
        console.log('lineLengths aggiornati:', updated);
        return updated;
      });
    } else {
      console.warn(`handleRatiosChange: valore non valido per subImageId ${subImageId}:`, numeric);
      // Se il valore non è valido, rimuovilo dai lineLengths
      setLineLengths(prev => {
        const updated = { ...prev };
        delete updated[subImageId];
        console.log('lineLengths dopo rimozione:', updated);
        return updated;
      });
    }
  }, []);


  // Calcolo del numero totale di denti esclusi
  const totalExcludedTeeth = excludedSubImages.length;

  useEffect(() => {
    const gender = sesso === 'M' ? 1 : 0;

    // Filtra la sotto-immagine con "lower_second_premolar" nel nome (il nome corretto generato)
    const secondPremolarSubImg = subImages.find(subImg =>
      subImg.subImage.name.includes("lower_second_premolar")
    );

    console.log('Cercando second premolar in subImages:', subImages.map(s => s.subImage.name));
    console.log('SubImages con ID:', subImages.map(s => ({ id: s.id, name: s.subImage.name })));
    console.log('Trovato secondPremolarSubImg:', secondPremolarSubImg);
    if (secondPremolarSubImg) {
      console.log('ID del second premolar:', secondPremolarSubImg.id);
    }
    console.log('lineLengths correnti:', lineLengths);

    // Ottieni il valore di lineLengths per questa sotto-immagine
    let F4_F5 = null;
    if (secondPremolarSubImg && secondPremolarSubImg.id in lineLengths) {
      const value = lineLengths[secondPremolarSubImg.id];
      console.log('Valore trovato per F4_F5:', value);
      if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
        F4_F5 = value;
      }
    }

    const F8 = totalExcludedTeeth;

    // Calcola B15 sommando i lineLengths delle sotto-immagini non escluse
    const B15 = subImages
      .filter(subImg => !excludedSubImages.includes(subImg.id))
      .reduce((acc, subImg) => {
        const value = lineLengths[subImg.id];
        if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
          return acc + value;
        }
        return acc;
      }, 0);

    console.log('Calcolo dell\'età con:', { gender, F4_F5, F8, B15 });

    if (F4_F5 !== null && F4_F5 > 0) {
      const estAgeValue = 8.971
        + (0.375 * gender)
        + (1.63 * F4_F5)
        + (0.674 * F8)
        - (1.034 * B15)
        - (0.176 * F8 * B15);
      
      // Verifica che il risultato sia valido
      if (!isNaN(estAgeValue) && isFinite(estAgeValue)) {
        setEstAge(estAgeValue.toFixed(2));
        console.log('Stima dell\'età calcolata:', estAgeValue.toFixed(2));
      } else {
        console.warn('Risultato del calcolo dell\'età non valido:', estAgeValue);
        setEstAge('N/A');
      }
    } else {
      console.warn(`F4_F5 non è un numero valido o è zero:`, F4_F5);
      setEstAge('N/A');
    }
  }, [sesso, lineLengths, totalExcludedTeeth, subImages, excludedSubImages]);


  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p>Uploading... Please Wait</p>
      </div>
    );
  }


  // Rendering condizionale in base allo stato
  if (currentStep === 'selection') {
    if (imageSize.width === 0 || imageSize.height === 0) {
      return <div className="loading-message">Caricamento dell'immagine...</div>;
    }


    return (
      <div className="selection-container">
        <div>
          <h2 className="step-title">
            {selectedPoints.length < maxTotalPoints
              ? pointTitles[selectedPoints.length]
              : "All points selected"}
          </h2>

          <CategorySelector
            onChange={handleCategoryChange}
            currentCategory={category}
            categories={colorCategories}
            categoryColors={categoryColors}
          />

          <div className="image-wrapper">
            <img
              src={loadedImage ? loadedImage.src : ''}
              alt="Uploaded"
              ref={imageRef}
              className="main-image"
            />
            <canvas
              ref={canvasRef}
              className="main-canvas"
              onClick={handleImageClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            />
          </div>
          <div className="instructions-container">
            <h2 className="step-title">
              {selectedPoints.length < pointTitles.length 
                ? pointTitles[selectedPoints.length] 
                : "All teeth selected"}
            </h2>
          </div>
          <div className="selected-points-container">
            <p className="points-count">
              Selected points: {selectedPoints.length} (Max {maxTotalPoints})
            </p>
          </div>
          <div className="buttons-container">
            <button
              onClick={handleUndoLastPoint}
              className="undo-button"
              title="Annulla Ultimo Punto"
            >
              Undo last selection
            </button>
            <button onClick={handleResetPoints} className="reset-button">
              Reset Points
            </button>
            {selectedPoints.length === maxTotalPoints && (
              <button onClick={handleVerification} className="verify-button">
                Save selection
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'annotation' && subImages.length > 0) {
    return (
      <div>
        {subImages.map((subImg) => (
          <SubImageAnnotator
            key={subImg.id}
            subImage={subImg.subImage}
            subImageId={subImg.id}
            toothType={subImg.subImage.toothType} // Passa il tipo di dente correttamente
            collectAnnotations={collectAnnotations}
            email={userEmail}
            tipo={tipo}
            posizione={posizione}
            sesso={sesso}
            realAge={realAge}
            initialAnnotations={allAnnotations[subImg.id]}
            updateSessionsCount={updateSessionsCount}
            onExcludeChange={handleExcludeChange}
            onRatiosChange={handleRatiosChange}
            isExcluded={excludedSubImages.includes(subImg.id)}
          />
        ))}
        
        <div className="results-footer">
          <div className="results-grid single-column">
            <div className="result-card">
              <h4>European Formula Analysis</h4>
              <div className="result-values">
                <div className="value-item">
                  <span className="value-number">-</span>
                  <span className="value-label">Min Age (yrs)</span>
                </div>
                <div className="value-divider">|</div>
                <div className="value-item estimate">
                  <span className="value-number">{estAge !== null ? estAge : 'N/A'}</span>
                  <span className="value-label">Estimated Age (yrs)</span>
                </div>
                <div className="value-divider">|</div>
                <div className="value-item">
                  <span className="value-number">-</span>
                  <span className="value-label">Max Age (yrs)</span>
                </div>
              </div>
              
              <div className="age-comparison-modern">
                <div className="value-item">
                  <span className="value">{totalExcludedTeeth}</span>
                  <span className="label">Excluded Teeth</span>
                </div>
                <div className="value-item">
                  <span className="value">{sesso}</span>
                  <span className="label">Gender</span>
                </div>
                {realAge && (
                  <div className="value-item real-age">
                    <span className="value">{realAge}</span>
                    <span className="label">Real Age (yrs)</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="actions-section">
            <button onClick={handleDownloadAnnotatedImages} className="action-button primary">
              <FaDownload className="button-icon" />
              <span>Download Images</span>
            </button>
            <button onClick={handleDownloadAnnotationsJSON} className="action-button secondary">
              <FaDownload className="button-icon" />
              <span>Download Annotations</span>
            </button>
          </div>
        </div>
        
        <div className="bibliography-section">
          <h4>Bibliography</h4>
          <div className="citation">
            <div className="title">Age estimation in children by measurement of open apices in teeth</div>
            <div className="authors">Cameriere, R., Ferrante, L. & Cingolani, M.</div>
            <div className="journal">Int J Legal Med 120, 49–52 (2006)</div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

ImageSelector.propTypes = {
  imageFile: PropTypes.object,
  userEmail: PropTypes.string,
  tipo: PropTypes.string,
  posizione: PropTypes.string,
  sesso: PropTypes.string,
  realAge: PropTypes.string,
  selectionRequired: PropTypes.bool,
  maxTotalPoints: PropTypes.number,
  colorCategories: PropTypes.arrayOf(PropTypes.string),
  categoryColors: PropTypes.object,
  passCategoryToAnnotation: PropTypes.bool,
  maxPointsPerCategory: PropTypes.object,
  useLeftRightInNames: PropTypes.bool,
  categoriesToAnnotate: PropTypes.arrayOf(PropTypes.string),
  loadedSession: PropTypes.object,
  loadedSubImages: PropTypes.array,
  updateSessionsCount: PropTypes.func,
  userInfo: PropTypes.object, // Assicurati che userInfo sia passato correttamente
};

export default ImageSelector;
